// 必要なライブラリやコンポーネントをインポート
import { useEffect, useRef, useState } from "react"; // Reactフック
import UserProblemsTemplate, { ChoiceType, PracticeType, TestType, SaveDataType } from "../templates/UserProblemsTemplate"; // 問題集テンプレートと型定義
import axios from "axios"; // HTTPリクエスト用
import { useSearchParams } from "react-router-dom"; // URLのクエリパラメータ取得用フック
import { useRecoilValue } from "recoil"; // グローバル状態管理用
import userState from "../../recoil/atoms/userState"; // ユーザー状態を管理するRecoilアトム

// 定数
const allowed = [8, 9, 10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31];

/** 
 * UserProblemsPageコンポーネント
 * ユーザー向けの「問題集」ページを表示するためのコンポーネント
 * ユーザーの問題集データを取得し、テンプレートに渡して表示
 */
const UserProblemsPage = () => {
    const [searchParams] = useSearchParams(); // URLのクエリパラメータを取得
    const userData = useRecoilValue(userState); // グローバルなユーザーデータを取得

    // 状態管理
    const [problems, setProblems] = useState<PracticeType[][]>([]); // 練習問題データ
    const [questions, setQuestions] = useState<TestType[]>([]); // テスト問題データ
    const [savedData, setSavedData] = useState<SaveDataType[]>([]) //中断データ
    const [isGetReady, setIsGetReady] = useState(false); // データ取得完了状態

    const initFlag = useRef(false); // 再レンダリング防止用フラグ

    /** 
     * 問題集のデータを取得する関数
     * 各カテゴリごとに練習問題を取得
     */
    const getGroup = async () => {
        const tmpList: PracticeType[][] = [];
        for (let i = 0; i < 4; i++) {
            await axios.post(process.env.REACT_APP_BE_HOST + 'workbooks/problems', {
                "userId": userData.userId,
                "questionId": i + 1
            }, {
                withCredentials: true, // 認証情報を含むリクエスト
            })
            .then((res: any) => {
                res.data?.sort((a: any, b: any) => a.questionBook - b.questionBook); // 問題集IDでソート
                const tmpProblems: PracticeType[] = [];
                res.data?.map((p: any) => {
                    tmpProblems.push({
                        practiceId: p.questionBook, // 問題集ID
                        practiceName: p.questionTitle ?? '', // 問題集名
                        triedCount: p.attemptCount ?? 0, // 挑戦回数
                        topic1: p.topic1??'', // トピック1
                        topic2: p.topic2??'',
                        topic3: p.topic3??'',
                        topic4: p.topic4??'',
                        topic5: p.topic5??'',
                        topic6: p.topic6??'',
                        topic7: p.topic7??'',
                        topic8: p.topic8??'',
                        topic9: p.topic9??'',
                        topic10: p.topic10??'',
                        topic11: p.topic11??'',
                        topic12: p.topic12??'',
                        topic13: p.topic13??'',
                        topic14: p.topic14??'',
                    });
                });
                tmpList.push(tmpProblems);               
            })
            .catch((e: any) => console.log(e)); // エラーハンドリング
        }
        setProblems(tmpList); // 練習問題データを状態に保存
    };

    /**
     * 問題集に関連付けられた画像データを取得する関数
     */
    const getImages = async () => {
        await axios.post(process.env.REACT_APP_BE_HOST + 'workbooks/questions', {
            "userId": userData.userId,
            "questionBook": savedData[0].questionBook,
            "noImage": false,
        }, {
            withCredentials: true
        })
        .then((res: any) => {
            const tmpQuestions: TestType[] = [];
            questions?.forEach((q) => {
                const question = res.data?.find((d: any) => d.questionId === q.testId);
                tmpQuestions.push({
                    ...q,
                    images: question.images, // 画像データを追加
                });
            });
            setQuestions(tmpQuestions); // テスト問題データを更新
        })
        .catch((e: any) => console.log(e));
    };

    /**
     * 初回レンダリング時に問題集データと質問データを取得
     */
    useEffect(() => {
        // 中断データ有り
        if (savedData?.length > 0) {
            let ignore = false; // メモリリーク防止用
            

            const getQuestions = async () => {                    
                // 中断データから再開
                if (window.confirm('前回の中断データから再開しますか？')) {

                    // 問題データ取得
                    await axios.post(process.env.REACT_APP_BE_HOST + 'workbooks/questions', {
                        "userId": userData.userId,
                        "questionBook": savedData[0].questionBook,
                        "noImage": true, // 画像なしデータを取得
                    }, {
                        withCredentials: true
                    })
                    .then((res: any) => {
                        if (!ignore) {
                            const tmpQuestions: TestType[] = [];
                            savedData[0].questionOrder?.map((s) => {
                                const q = res.data?.find((item: any) => item.questionId === s);
                                const choices: ChoiceType[] = q.choices;
                                // 初級コースの場合は選択しをシャッフルしない
                                if(!allowed.includes(savedData[0].questionBook)){
                                    // 選択肢をシャッフル
                                    choices?.sort(() => 0.5 - Math.random());
                                } else {
                                    choices?.sort((a, b) => a.choiceNum - b.choiceNum);
                                }
                                const answer: number[] = [];
                                choices?.forEach((c: ChoiceType, index) => {
                                    if (c.isCorrect) {
                                        answer.push(index); // 正解のインデックスを収集
                                    }
                                });
                                tmpQuestions.push({
                                    testId: q.questionId, // 質問ID
                                    question: q.questionText ? String(q.questionText).replaceAll('\\r','\r').replaceAll('\\n','\n') : '', // 質問テキスト
                                    choices: choices, // 選択肢
                                    answer: answer, // 正解
                                    explanation: q.explanation ? String(q.explanation).replaceAll('\\r','\r').replaceAll('\\n','\n') : '', // 解説
                                    images: q.images, // 画像データ
                                });
                            });
                            // 質問データをシャッフルして保存
                            //tmpQuestions?.sort(() => 0.5 - Math.random());
                            setQuestions(tmpQuestions);
                            setIsGetReady(true); // データ取得完了状態を更新
                        }
                    })
                    .catch((e: any) => console.log(e));
                }
                // 中断データ削除
                axios.post(process.env.REACT_APP_BE_HOST + 'tmpData/InterruptedDelete', {
                    "userId": userData.userId,
                }, {
                    withCredentials: true
                }).then((res: any) => {}).catch((e: any) => console.log(e));
            };

            if (!initFlag.current && questions.length === 0) {
                getQuestions(); // 質問データを取得
                initFlag.current = true;
            }

            return () => {
                ignore = true; // クリーンアップ
            };
        }
    }, [savedData]);

    /**
     * 画像データの取得処理をトリガー
     */
    useEffect(() => {
        if (isGetReady) {
            getImages(); // 画像データを取得
        }
    }, [isGetReady]);

    useEffect(() => {
        getGroup(); // 問題集データを取得
        // 中断データ取得
        axios.post(process.env.REACT_APP_BE_HOST + 'tmpData/InterruptedDataRetrieval', {
            "userId": userData.userId,
        }, {
            withCredentials: true
        }).then((res: any) => {
            if (res.data) {
                const choiceNums: (string|null)[] = [];
                const questionOrder: number[] = [];
                if (res.data.questionNum1 > 0) { choiceNums.push(res.data.choiceNum1); questionOrder.push(res.data.questionNum1);}
                if (res.data.questionNum2 > 0) { choiceNums.push(res.data.choiceNum2); questionOrder.push(res.data.questionNum2);}
                if (res.data.questionNum3 > 0) { choiceNums.push(res.data.choiceNum3); questionOrder.push(res.data.questionNum3);}
                if (res.data.questionNum4 > 0) { choiceNums.push(res.data.choiceNum4); questionOrder.push(res.data.questionNum4);}
                if (res.data.questionNum5 > 0) { choiceNums.push(res.data.choiceNum5); questionOrder.push(res.data.questionNum5);}
                if (res.data.questionNum6 > 0) { choiceNums.push(res.data.choiceNum6); questionOrder.push(res.data.questionNum6);}
                if (res.data.questionNum7 > 0) { choiceNums.push(res.data.choiceNum7); questionOrder.push(res.data.questionNum7);}
                if (res.data.questionNum8 > 0) { choiceNums.push(res.data.choiceNum8); questionOrder.push(res.data.questionNum8);}
                if (res.data.questionNum9 > 0) { choiceNums.push(res.data.choiceNum9); questionOrder.push(res.data.questionNum9);}
                if (res.data.questionNum10 > 0) { choiceNums.push(res.data.choiceNum10); questionOrder.push(res.data.questionNum10);}
                if (res.data.questionNum11 > 0) { choiceNums.push(res.data.choiceNum11); questionOrder.push(res.data.questionNum11);}
                if (res.data.questionNum12 > 0) { choiceNums.push(res.data.choiceNum12); questionOrder.push(res.data.questionNum12);}
                if (res.data.questionNum13 > 0) { choiceNums.push(res.data.choiceNum13); questionOrder.push(res.data.questionNum13);}
                if (res.data.questionNum14 > 0) { choiceNums.push(res.data.choiceNum14); questionOrder.push(res.data.questionNum14);}
                if (res.data.questionNum15 > 0) { choiceNums.push(res.data.choiceNum15); questionOrder.push(res.data.questionNum15);}
                if (res.data.questionNum16 > 0) { choiceNums.push(res.data.choiceNum16); questionOrder.push(res.data.questionNum16);}
                if (res.data.questionNum17 > 0) { choiceNums.push(res.data.choiceNum17); questionOrder.push(res.data.questionNum17);}
                if (res.data.questionNum18 > 0) { choiceNums.push(res.data.choiceNum18); questionOrder.push(res.data.questionNum18);}
                if (res.data.questionNum19 > 0) { choiceNums.push(res.data.choiceNum19); questionOrder.push(res.data.questionNum19);}
                if (res.data.questionNum20 > 0) { choiceNums.push(res.data.choiceNum20); questionOrder.push(res.data.questionNum20);}
                if (res.data.questionNum21 > 0) { choiceNums.push(res.data.choiceNum21); questionOrder.push(res.data.questionNum21);}
                if (res.data.questionNum22 > 0) { choiceNums.push(res.data.choiceNum22); questionOrder.push(res.data.questionNum22);}
                if (res.data.questionNum23 > 0) { choiceNums.push(res.data.choiceNum23); questionOrder.push(res.data.questionNum23);}
                if (res.data.questionNum24 > 0) { choiceNums.push(res.data.choiceNum24); questionOrder.push(res.data.questionNum24);}
                if (res.data.questionNum25 > 0) { choiceNums.push(res.data.choiceNum25); questionOrder.push(res.data.questionNum25);}
                if (res.data.questionNum26 > 0) { choiceNums.push(res.data.choiceNum26); questionOrder.push(res.data.questionNum26);}
                if (res.data.questionNum27 > 0) { choiceNums.push(res.data.choiceNum27); questionOrder.push(res.data.questionNum27);}
                if (res.data.questionNum28 > 0) { choiceNums.push(res.data.choiceNum28); questionOrder.push(res.data.questionNum28);}
                if (res.data.questionNum29 > 0) { choiceNums.push(res.data.choiceNum29); questionOrder.push(res.data.questionNum29);}
                if (res.data.questionNum30 > 0) { choiceNums.push(res.data.choiceNum30); questionOrder.push(res.data.questionNum30);}
                if (res.data.questionNum31 > 0) { choiceNums.push(res.data.choiceNum31); questionOrder.push(res.data.questionNum31);}
                if (res.data.questionNum32 > 0) { choiceNums.push(res.data.choiceNum32); questionOrder.push(res.data.questionNum32);}
                if (res.data.questionNum33 > 0) { choiceNums.push(res.data.choiceNum33); questionOrder.push(res.data.questionNum33);}
                if (res.data.questionNum34 > 0) { choiceNums.push(res.data.choiceNum34); questionOrder.push(res.data.questionNum34);}
                if (res.data.questionNum35 > 0) { choiceNums.push(res.data.choiceNum35); questionOrder.push(res.data.questionNum35);}
                if (res.data.questionNum36 > 0) { choiceNums.push(res.data.choiceNum36); questionOrder.push(res.data.questionNum36);}
                if (res.data.questionNum37 > 0) { choiceNums.push(res.data.choiceNum37); questionOrder.push(res.data.questionNum37);}
                if (res.data.questionNum38 > 0) { choiceNums.push(res.data.choiceNum38); questionOrder.push(res.data.questionNum38);}
                if (res.data.questionNum39 > 0) { choiceNums.push(res.data.choiceNum39); questionOrder.push(res.data.questionNum39);}
                if (res.data.questionNum40 > 0) { choiceNums.push(res.data.choiceNum40); questionOrder.push(res.data.questionNum40);}
                if (res.data.questionNum41 > 0) { choiceNums.push(res.data.choiceNum41); questionOrder.push(res.data.questionNum41);}
                if (res.data.questionNum42 > 0) { choiceNums.push(res.data.choiceNum42); questionOrder.push(res.data.questionNum42);}
                if (res.data.questionNum43 > 0) { choiceNums.push(res.data.choiceNum43); questionOrder.push(res.data.questionNum43);}
                if (res.data.questionNum44 > 0) { choiceNums.push(res.data.choiceNum44); questionOrder.push(res.data.questionNum44);}
                if (res.data.questionNum45 > 0) { choiceNums.push(res.data.choiceNum45); questionOrder.push(res.data.questionNum45);}
                if (res.data.questionNum46 > 0) { choiceNums.push(res.data.choiceNum46); questionOrder.push(res.data.questionNum46);}
                if (res.data.questionNum47 > 0) { choiceNums.push(res.data.choiceNum47); questionOrder.push(res.data.questionNum47);}
                if (res.data.questionNum48 > 0) { choiceNums.push(res.data.choiceNum48); questionOrder.push(res.data.questionNum48);}
                if (res.data.questionNum49 > 0) { choiceNums.push(res.data.choiceNum49); questionOrder.push(res.data.questionNum49);}
                if (res.data.questionNum50 > 0) { choiceNums.push(res.data.choiceNum50); questionOrder.push(res.data.questionNum50);}
                if (res.data.questionNum51 > 0) { choiceNums.push(res.data.choiceNum51); questionOrder.push(res.data.questionNum51);}
                if (res.data.questionNum52 > 0) { choiceNums.push(res.data.choiceNum52); questionOrder.push(res.data.questionNum52);}
                if (res.data.questionNum53 > 0) { choiceNums.push(res.data.choiceNum53); questionOrder.push(res.data.questionNum53);}
                if (res.data.questionNum54 > 0) { choiceNums.push(res.data.choiceNum54); questionOrder.push(res.data.questionNum54);}
                if (res.data.questionNum55 > 0) { choiceNums.push(res.data.choiceNum55); questionOrder.push(res.data.questionNum55);}
                if (res.data.questionNum56 > 0) { choiceNums.push(res.data.choiceNum56); questionOrder.push(res.data.questionNum56);}
                if (res.data.questionNum57 > 0) { choiceNums.push(res.data.choiceNum57); questionOrder.push(res.data.questionNum57);}
                if (res.data.questionNum58 > 0) { choiceNums.push(res.data.choiceNum58); questionOrder.push(res.data.questionNum58);}
                if (res.data.questionNum59 > 0) { choiceNums.push(res.data.choiceNum59); questionOrder.push(res.data.questionNum59);}
                if (res.data.questionNum60 > 0) { choiceNums.push(res.data.choiceNum60); questionOrder.push(res.data.questionNum60);}
                if (res.data.questionNum61 > 0) { choiceNums.push(res.data.choiceNum61); questionOrder.push(res.data.questionNum61);}
                if (res.data.questionNum62 > 0) { choiceNums.push(res.data.choiceNum62); questionOrder.push(res.data.questionNum62);}
                if (res.data.questionNum63 > 0) { choiceNums.push(res.data.choiceNum63); questionOrder.push(res.data.questionNum63);}
                if (res.data.questionNum64 > 0) { choiceNums.push(res.data.choiceNum64); questionOrder.push(res.data.questionNum64);}
                if (res.data.questionNum65 > 0) { choiceNums.push(res.data.choiceNum65); questionOrder.push(res.data.questionNum65);}
                if (res.data.questionNum66 > 0) { choiceNums.push(res.data.choiceNum66); questionOrder.push(res.data.questionNum66);}
                if (res.data.questionNum67 > 0) { choiceNums.push(res.data.choiceNum67); questionOrder.push(res.data.questionNum67);}
                if (res.data.questionNum68 > 0) { choiceNums.push(res.data.choiceNum68); questionOrder.push(res.data.questionNum68);}
                if (res.data.questionNum69 > 0) { choiceNums.push(res.data.choiceNum69); questionOrder.push(res.data.questionNum69);}
                if (res.data.questionNum70 > 0) { choiceNums.push(res.data.choiceNum70); questionOrder.push(res.data.questionNum70);}

                setSavedData([{
                    questionBook: res.data.questionBook || 0,
                    questionNums: res.data.questionNum || 0,
                    choiceNums,
                    questionOrder,
                    elapsed: !allowed.includes(res.data.questionBook) ? res.data.elapsed:"00:00:00",
//                    elapsed: res.data.elapsed,
                }]);
            }
        }).catch((e: any) => console.log(e));

    }, [])

    // テンプレートコンポーネントをレンダリング
    return (
        <>
            {questions.length > 0 ? <UserProblemsTemplate 
                practiceList={problems} 
                tests={questions} 
                questionBook={savedData[0].questionBook} 
                userId={userData.userId}
                saveData={savedData[0]}
            />
            :
            <UserProblemsTemplate 
                practiceList={problems} 
                tests={questions} 
                questionBook={0} 
                userId={userData.userId}
            />}
        </>
    );
};

// UserProblemsPageコンポーネントをデフォルトエクスポート
export default UserProblemsPage;
